import { useCallback, useEffect, useMemo, useState } from "react"
import { usePrevious } from "react-use"

import { useIntersection } from "src/components/hooks/useIntersection"

const INITIAL_PAGE = 1
const OPTIONS = {}

export const usePseudoLoadMore = <T>(
  rawData: T[],
  limit: number,
  predicate?: (value: T, index: number, array: T[]) => boolean,
) => {
  const [buttonElement, setButtonElement] = useState<HTMLButtonElement | null>(
    null,
  )

  const [page, setPage] = useState(INITIAL_PAGE)

  const intersection = useIntersection<HTMLButtonElement>(
    buttonElement,
    OPTIONS,
  )
  const prevIntersection = usePrevious(intersection)

  const data = useMemo(() => {
    if (!predicate) {
      return rawData
    }

    return rawData.filter(predicate)
  }, [predicate, rawData])

  const currentData = useMemo(() => {
    return data.slice(0, page * limit)
  }, [data, page, limit])

  const hasNext = useMemo(() => {
    return data.length > page * limit
  }, [data, limit, page])

  const onLoadMore = useCallback(() => {
    setPage((page) => page + 1)
  }, [])

  useEffect(() => {
    setPage(INITIAL_PAGE)
  }, [data])

  useEffect(() => {
    if (
      hasNext &&
      intersection?.isIntersecting &&
      !prevIntersection?.isIntersecting
    ) {
      onLoadMore()
    }
  }, [
    hasNext,
    intersection?.isIntersecting,
    onLoadMore,
    prevIntersection?.isIntersecting,
  ])

  return { data: currentData, hasNext, setButtonElement, onLoadMore }
}
